/**
 * ReportNav - Section navigation for the public workshop report.
 * Desktop: fixed left sidebar (≥1200px). Tablet/mobile: hamburger + slide-in panel.
 * Hidden on print.
 */
import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { Menu, X, Printer } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation.jsx';

const MOBILE_BREAKPOINT = '1200px';

const sidebarBase = `
  display: flex;
  flex-direction: column;
  gap: 2px;
  z-index: 50;
`;

const DesktopSidebar = styled.nav`
  ${sidebarBase}
  position: fixed;
  top: 80px;
  left: 0;
  width: 220px;
  height: calc(100vh - 80px);
  padding: 24px 0 24px 16px;
  overflow-y: auto;
  opacity: ${p => (p.$visible ? 1 : 0)};
  transform: translateX(${p => (p.$visible ? '0' : '-20px')});
  pointer-events: ${p => (p.$visible ? 'auto' : 'none')};
  transition: opacity 0.3s ease, transform 0.3s ease;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 2px; }

  @media print { display: none; }
  @media (max-width: ${MOBILE_BREAKPOINT}) { display: none; }
`;

const MobileToggle = styled.button`
  display: none;
  position: fixed;
  top: 88px;
  left: 12px;
  z-index: 60;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #374151;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  opacity: ${p => (p.$visible ? 1 : 0)};
  pointer-events: ${p => (p.$visible ? 'auto' : 'none')};
  transition: opacity 0.3s ease;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: flex;
  }

  @media print { display: none; }
`;

const MobileOverlay = styled.div`
  display: none;
  position: fixed;
  inset: 0;
  z-index: 70;
  background: rgba(0, 0, 0, 0.35);

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: ${p => (p.$open ? 'block' : 'none')};
  }

  @media print { display: none; }
`;

const MobilePanel = styled.nav`
  ${sidebarBase}
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  width: min(280px, 85vw);
  height: 100vh;
  padding: 72px 0 24px 16px;
  background: #fff;
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.12);
  overflow-y: auto;
  transform: translateX(${p => (p.$open ? '0' : '-100%')});
  transition: transform 0.25s ease;
  z-index: 80;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: flex;
  }

  @media print { display: none; }
`;

const NavItem = styled.button`
  background: none;
  border: none;
  border-left: 2px solid ${p => (p.$active ? '#367ABA' : 'transparent')};
  padding: 8px 12px;
  font-size: 13px;
  font-weight: ${p => (p.$active ? 600 : 400)};
  color: ${p => (p.$active ? '#000' : '#6b7280')};
  text-align: left;
  cursor: pointer;
  transition: all 0.15s;
  line-height: 1.4;
  min-height: 44px;

  &:hover {
    color: #000;
    border-left-color: #d1d5db;
  }
`;

const PrintButton = styled.button`
  background: none;
  border: 1px solid #e5e7eb;
  border-radius: 2px;
  padding: 8px 12px;
  font-size: 12px;
  color: #6b7280;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  margin: auto 12px 0 12px;
  min-height: 44px;

  &:hover {
    background: #f9fafb;
    color: #000;
  }
`;

function NavContents({ sections, activeId, onNavigate, printLabel }) {
  return (
    <>
      {sections.map(s => (
        <NavItem
          key={s.id}
          $active={activeId === s.id}
          onClick={() => onNavigate(s.id)}
        >
          {s.label}
        </NavItem>
      ))}
      <PrintButton onClick={() => window.print()}>
        <Printer size={14} />
        {printLabel}
      </PrintButton>
    </>
  );
}

export default function ReportNav({ sections = [] }) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const observerRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > 350);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!sections.length) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: '-20% 0px -60% 0px' }
    );

    sections.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observerRef.current.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, [sections]);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileOpen]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setMobileOpen(false);
  };

  const printLabel = t('report.topbar.printPdf');

  return (
    <>
      <DesktopSidebar $visible={visible}>
        <NavContents
          sections={sections}
          activeId={activeId}
          onNavigate={scrollTo}
          printLabel={printLabel}
        />
      </DesktopSidebar>

      <MobileToggle
        type="button"
        $visible={visible}
        onClick={() => setMobileOpen(o => !o)}
        aria-expanded={mobileOpen}
        aria-label={mobileOpen ? t('report.nav.closeMenu') : t('report.nav.openMenu')}
      >
        {mobileOpen ? <X size={20} /> : <Menu size={20} />}
      </MobileToggle>

      <MobileOverlay $open={mobileOpen} onClick={() => setMobileOpen(false)} aria-hidden="true" />

      <MobilePanel $open={mobileOpen} aria-label={t('report.nav.menuTitle')}>
        <NavContents
          sections={sections}
          activeId={activeId}
          onNavigate={scrollTo}
          printLabel={printLabel}
        />
      </MobilePanel>
    </>
  );
}
